import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { createElement } from 'react'
import { createRoot } from 'react-dom/client'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { installStandaloneRecovery } from '@/lib/standalone-recovery'
import { BRAND } from '@/lib/brand'

type PageModule = { default: ResolvedComponent }

// Recover iOS Home Screen (standalone PWA) sessions from a dead/suspended
// WebKit context. Client-only — never imported by the SSR entrypoint.
installStandaloneRecovery()

createInertiaApp({
  // The browser tab always names the product. A page's own <Head title> is
  // suffixed; the public landing page, which titles itself with the brand,
  // is left alone rather than reading "IUPAT Portal · IUPAT Portal".
  title: (title) => (title && title !== BRAND ? `${title} · ${BRAND}` : BRAND),

  resolve: (name) => {
    const pages = import.meta.glob<PageModule>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      // Vite materializes this glob once, at boot. A page file added while the
      // dev server was not watching it is simply absent here — see the
      // usePolling note in vite.config.ts. Throwing beats returning undefined,
      // which renders a blank screen against a perfectly healthy 200.
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }
    return page
  },

  setup({ el, App, props }) {
    if (el) {
      createRoot(el).render(
        createElement(ErrorBoundary, null, createElement(App, props)),
      )
    } else {
      console.error(
        'Missing root element. Move `vite_typescript_tag "inertia"` into an Inertia-specific layout.',
      )
    }
  },
})
